import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import { i18n } from "next-i18next"
import { useRouter } from "next/router"

import {
  FAQ_PAGE_DATA_URL,
  authorBaseUrl,
  footerUrl,
  headerUrl,
  previewContentPrefix,
  PRIVACY_ASPATH,
  DISABLE_SROLL_PATH,
  pageNotFoundDataUrl,
  HELP_AND_FAQ,
  INSTALLATION_SERVICES_ASPATH,
  DEFAULT_SCROLL,
  BROWSE_ALL_STORES,
  BROWSE_ALL_STORES_URL,
  LOCATIONS_ASPATH,
  COMPONENT_TYPES,
} from "@/constants"
import { aemAxiosInstance, aemPreviewAxiosInstance } from "@/constants/api"

import useIsSsr from "@/hooks/useIsSsr"
import usePageAuth from "@/hooks/usePageAuth"

import {
  getScriptsAndStyles,
  removeAndCleanLink,
  filterBackEndJson,
  getClienIP,
  getDataLayerObj,
  getOffsetTop,
  performRedirect,
} from "@/utils/helper"

import {
  setGbhDataLayerCustom,
  setPageIsIntercative,
} from "@/store/features/genericSlice"

import AemGrid from "@/components/AemGrid"
import NextHead from "@/components/core/Head/NextHead"
import BreadCrumb from "@/components/Default/BreadCrumb"
import ContactUs from "@/components/ContactUs/v1/ContactUs"
import KeySpecifier from "@/components/KeySpecifier/v1/KeySpecifier"
import AssociateMembership from "@/components/AssociateMembership/v1/AssociateMembership"
import Literature from "@/components/Literature/v1/Literature"
import TechnicalSpecs from "@/components/TechnicalSpecs/v1/TechnicalSpecs"
import ProductAccordion from "@/components/ProductDetail/v3/ProductAccordion"
import GlobalPageHeader from "@/components/Shared/GlobalPage/GlobalPageHeader"
import GlobalPageFooter from "@/components/Shared/GlobalPage/GlobalPageFooter"
import KnowledgeArticle from "@/components/KnowledgeArticle/v1/KnowledgeArticle"
import KnowledgeArticleFiller from "@/components/KnowledgeArticle/v1/KnowledgeArticleFiller"
import BrowseAllStores from "@/components/BrowseAllStores/v1/BrowseAllStores"
import FriendsAndFamily from "@/components/FriendsAndFamily/v1/FriendsAndFamily"
import InstallDesignServices from "@/components/InstallDesignServices/v1/InstallDesignServices"
import FindStore from "@/components/FindStore/v1/FindStore"
import LeadGen from "@/components/LeadGeneration"
import FindShowroom from "@/components/Findashowroom"
import InspirationLanding from "@/components/InspirationLanding/v1/InspirationLanding"
import InspirationDetail from "@/components/InspirationDetail/v1/InspirationDetail"
import TradeMembership from "@/components/TradeMembership"
/**
 * renders home page
 * @return {JSX.Element}
 */
function Home({
  tmpData: data,
  headertmp: header,
  footertmp: footer,
  ip,
  globalScript,
  footerScript,
  analyticsData,
  gbhDataLayerCustom,
  fullUrl,
  urlNames,
  data404,
}) {
  const dispatch = useDispatch()
  const router = useRouter()
  const isSsr = useIsSsr()
  const [pageRedirect, setPageRedirect] = useState("")
  const [isScrollMarginRequired, setIsScrollMarginRequired] = useState(false)
  const { pageUrl = "", redirectTarget: { page: { path = "" } = {} } = {} } =
    data

  const { scroll } = DEFAULT_SCROLL
  useEffect(() => {
    if (pageUrl) {
      const containsString = DISABLE_SROLL_PATH.some(str =>
        pageUrl.includes(str)
      )
      const installServiceHash =
        (router?.asPath?.includes(INSTALLATION_SERVICES_ASPATH) &&
          router?.asPath?.includes("#")) ??
        false

      if (containsString) {
        window.scrollTo({
          top: 1,
          behavior: "smooth",
        })
      }

      if (installServiceHash) {
        setTimeout(() => {
          const hashValue = router?.asPath?.split("#")[1] ?? ""
          const element = document.querySelector(`#${hashValue}`) ?? {}
          const offsetTop = getOffsetTop(element) - scroll
          window.scrollTo({
            top: offsetTop,
            behavior: "smooth",
          })
        }, 3000)
      }
    }
  }, [pageUrl])

  useEffect(() => {
    if (ip) sessionStorage.setItem("true-ip", ip)
  }, [ip])

  usePageAuth()
  useEffect(() => {
    dispatch(setGbhDataLayerCustom(gbhDataLayerCustom))
    if (router?.asPath?.includes(PRIVACY_ASPATH)) {
      dispatch(setPageIsIntercative(true))
    }
    if (router?.asPath?.includes(INSTALLATION_SERVICES_ASPATH)) {
      setIsScrollMarginRequired(true)
    }
    return () => dispatch(setGbhDataLayerCustom(""))
  }, [])
  useEffect(() => {
    getDataLayerObj(analyticsData)
  }, [analyticsData])

  useEffect(() => {
    if (path) {
      setPageRedirect(removeAndCleanLink(path))
    }
  }, [path])
  useEffect(() => {
    if (!isSsr && pageRedirect) {
      router.push(pageRedirect)
    }
  }, [isSsr, pageRedirect])
  useEffect(() => {
    getScriptsAndStyles(data)
  }, [data])

  return (
    <>
      {data ? <NextHead {...data} fullUrl={fullUrl} /> : null}
      {header ? (
        <GlobalPageHeader
          header={header}
          title={data.title}
          globalScripting={globalScript}
        />
      ) : null}
      {data ? (
        <AemGrid
          isScrollMarginRequired={isScrollMarginRequired}
          data={data}
          count={0}
          breadCrumb={(data, fullData) => (
            <BreadCrumb data={data} fullData={fullData} />
          )}
          productAccordion={(data, fullData) => (
            <ProductAccordion data={data} fullData={fullData} />
          )}
          contactUsFiller={() => <KnowledgeArticleFiller />}
          contactUs={data => <ContactUs data={data} />}
          leadGeneration={data => <LeadGen data={data} />}
          tradeMembership={data => <TradeMembership data={data} />}
          keySpecifier={data => <KeySpecifier data={data} />}
          associateMembership={data => <AssociateMembership data={data} />}
          friendsAndFamily={data => <FriendsAndFamily data={data} />}
          literature={data => <Literature data={data} />}
          inspirationLanding={data => <InspirationLanding data={data} />}
          inspirationDetail={data => {
            if (router?.asPath?.includes(COMPONENT_TYPES.INSPIRATION_DETAIL)) {
              return (
                <div
                  data-component="InspirationDetail"
                  data-static-text={JSON?.stringify(data)}
                >
                  <InspirationDetail data={data} isDetailsPage={true} />
                </div>
              )
            } else {
              return (
                <div
                  data-component="InspirationDetail"
                  data-static-text={JSON?.stringify(data)}
                ></div>
              )
            }
          }}
          technicalSpecifications={data => (
            <div id="main-content">
              <TechnicalSpecs data={data} />
            </div>
          )}
          knowledgeArticle={(data, urlNames) => (
            <KnowledgeArticle
              data={data}
              urlNames={urlNames}
              data404={data404}
            />
          )}
          knowledgeArticleFiller={() => <KnowledgeArticleFiller />}
          urlNames={urlNames}
          browseAllStores={data => <BrowseAllStores data={data} />}
          installDesignServices={data => <InstallDesignServices data={data} />}
          findStore={data => <FindStore data={data} />}
          findashowroom={data => <FindShowroom data={data} />}
        />
      ) : null}
      {footer ? (
        <GlobalPageFooter footer={footer} footerScripting={footerScript} />
      ) : null}
    </>
  )
}

/**
 *
 * @return {Object}
 */
export async function getServerSideProps({ req, res, locale, query }) {
  await i18n?.reloadResources()
  const ip = getClienIP(req)
  const urlPath = req.url.split("?")[0] ?? ""
  const { redirectUrl = "" } = performRedirect(req?.url, locale) ?? {}
  if (redirectUrl) {
    return {
      redirect: {
        destination: redirectUrl,
        permanent: true,
      },
    }
  }
  const isPreviewMode = req.cookies["aem-author-preview-mode"]
  const { queryAspath = "" } = query ?? {}
  const tempUrl = req?.url?.includes(HELP_AND_FAQ)
    ? FAQ_PAGE_DATA_URL
    : req?.url?.includes(BROWSE_ALL_STORES)
    ? BROWSE_ALL_STORES_URL
    : queryAspath?.includes(LOCATIONS_ASPATH)
    ? LOCATIONS_ASPATH
    : req?.url?.split("?")[0]
  const reqUrl = tempUrl?.includes(FAQ_PAGE_DATA_URL)
    ? FAQ_PAGE_DATA_URL
    : tempUrl?.includes(BROWSE_ALL_STORES_URL)
    ? BROWSE_ALL_STORES_URL
    : tempUrl

  const fullUrl = `${authorBaseUrl}/${locale}${reqUrl}`
  const urlNames = query["urlNames"] ? query["urlNames"] : ""

  let data
  try {
    const response = isPreviewMode
      ? await aemPreviewAxiosInstance({
          url: `${previewContentPrefix}/${locale}${reqUrl}.model.json`,
          method: "GET",
        })
      : await aemAxiosInstance({
          url: `/${locale}${reqUrl}.model.json`,
          method: "GET",
        })
    data = response?.data
  } catch (err) {
    return {
      notFound: true,
    }
  }

  // For header
  let header = {}
  try {
    const headerResponse = isPreviewMode
      ? await aemPreviewAxiosInstance({
          url: `${data.headerExpFrg}.model.json`,
          method: "GET",
        })
      : await aemAxiosInstance({
          url: `${data.headerExpFrg}.model.json`,
          method: "GET",
        })
    if (headerResponse?.status === 200) {
      header = headerResponse?.data ?? {}
    } else {
      console.error(
        "Non-successful header response status:",
        headerResponse.status
      )
    }
  } catch (error) {
    // Handle the error here (e.g., log it or take other actions)
    console.error("Error fetching header data:", error)

    // If the request failed, fall back to headerUrl
    try {
      const headerFallbackResponse = isPreviewMode
        ? await aemPreviewAxiosInstance({
            url: `${headerUrl}.model.json`,
            method: "GET",
          })
        : await aemAxiosInstance({
            url: `${headerUrl}.model.json`,
            method: "GET",
          })
      if (headerFallbackResponse.status === 200) {
        header = headerFallbackResponse.data
      } else {
        console.error(
          "Non-successful header response status:",
          headerFallbackResponse.status
        )
      }
    } catch (fallbackError) {
      // Handle the fallback error here if needed
      console.error(
        "Error fetching header data from fallback URL:",
        fallbackError
      )
    }
  }

  let footer = {}

  try {
    const footerResponse = isPreviewMode
      ? await aemPreviewAxiosInstance({
          url: `${data.footerExpFrg}.model.json`,
          method: "GET",
        })
      : await aemAxiosInstance({
          url: `${data.footerExpFrg}.model.json`,
          method: "GET",
        })
    if (footerResponse.status === 200) {
      footer = footerResponse.data
    } else {
      console.error(
        "Non-successful footerResponse response status:",
        footerResponse.status
      )
    }
  } catch (error) {
    // Handle the error here (e.g., log it or take other actions)
    console.error("Error fetching header data:", error)

    // If the request failed, fall back to headerUrl
    try {
      const footerFallbackResponse = isPreviewMode
        ? await aemPreviewAxiosInstance({
            url: `${footerUrl}.model.json`,
            method: "GET",
          })
        : await aemAxiosInstance({
            url: `${footerUrl}.model.json`,
            method: "GET",
          })
      if (footerFallbackResponse.status === 200) {
        header = footerFallbackResponse.data
      } else {
        console.log(
          "Non-successful footer response status:",
          footerFallbackResponse.status
        )
      }
    } catch (fallbackError) {
      // Handle the fallback error here if needed
      console.error(
        "Error fetching header data from fallback URL:",
        fallbackError
      )
    }
  }

  const { data: { pageComponentProperties: pageData404 = {} } = {} } =
    await aemAxiosInstance({
      url: pageNotFoundDataUrl,
      method: "GET",
    })
  const globalScript = data?.globalScript ? data?.globalScript : null
  const footerScript = data?.footerScript ? data?.footerScript : null
  const topHeadScript = data?.topHeadScript ? data?.topHeadScript : null
  const analyticsScriptClass = data?.analyticsScriptClass
    ? data?.analyticsScriptClass
    : null

  let tmpData = {}
  if (data.pageComponentProperties) {
    tmpData = data.pageComponentProperties
  } else {
    tmpData = data
  }

  const analyticsData = data?.data ? data?.data : null

  const gbhDataLayerCustom = {}
  if (data?.datalayerPageName) {
    gbhDataLayerCustom.datalayerPageName = data?.datalayerPageName
  }
  if (data?.eventPageType) {
    gbhDataLayerCustom.eventPageType = data?.eventPageType
  }
  if (
    !data?.pageComponentProperties?.description &&
    data?.pageComponentProperties?.dataLayer &&
    data?.pageComponentProperties?.dataLayer[data?.pageComponentProperties?.id]
  ) {
    gbhDataLayerCustom.dataLayer =
      data?.pageComponentProperties?.dataLayer[
        data?.pageComponentProperties?.id
      ]
  }
  tmpData = filterBackEndJson(tmpData)
  if (data?.pageUrl) {
    tmpData["pageUrl"] = data?.pageUrl
  }
  const headertmp = filterBackEndJson(header)
  const footertmp = filterBackEndJson(footer)
  const data404 = filterBackEndJson(pageData404)
  const metaContent = data?.metaContent ?? ""
  const canonicalUrl = data?.canonicalUrl ?? ""
  // Pass data to the page via props
  return {
    props: {
      ip,
      tmpData,
      headertmp,
      footertmp,
      globalScript,
      footerScript,
      topHeadScript,
      analyticsData,
      analyticsScriptClass,
      gbhDataLayerCustom,
      fullUrl,
      urlNames,
      data404,
      urlPath,
      metaContent,
      canonicalUrl,
      ...(await serverSideTranslations(locale, ["common"])),
    },
  }
}

export default Home
